<template>
	<div class="container pb-11">
		<div class="md:grid grid-cols-2 gap-6 lg:gap-16">
			<InformationAboutHypermarket text="Informace pro" class="md:hidden col-span-2 rem:max-w-[488px] mx-auto mb-3 xs:mb-5" :default-hm="DEFAULT_HYPERMARKET" />
			<div class="mx-auto w-full max-md:rem:max-w-[488px]">
				<div class="relative flex justify-center items-start w-full h-full rem:max-h-[400px]">
					<ProductRibbons
						:product="product"
						class="absolute left-0 top-0 z-10 overflow-hidden"
						is-product-detail
					/>
					<ProductImage
						:product="product"
						variant="detail"
						class="max-md:w-auto rem:max-h-[400px] max-md:rem:max-h-[250px] max-md:object-cover w-full h-full"
					/>
					<ProductBadges
						v-if="badges"
						class="absolute inset-0"
						:product="product"
						:badges="badges"
						:age-limitation="product.ageLimitation"
					/>
					<ProductOutOfStockBar :product="product" />
				</div>
				<LazyProductDetailMoreVariants
					v-if="moreVariants?.length"
					:variants="moreVariants"
					:current-variant="product"
					:route-generator="productVariantRouteGenerator"
					class="mb-6 md:mt-14"
				/>
			</div>
			<div class="rem:max-w-[488px] max-md:mx-auto">
				<div class="max-md:mx-auto max-md:relative">
					<div class="flex flex-col gap-2 lg:gap-4 max-md:justify-between auto-cols-mins mb-5 max-md:mt-4">
						<div class="mt-auto col-span-2">
							<h1
								class="typo-h1 text-body-primary"
							>
								{{ product.name }}
							</h1>
						</div>

						<ProductAmountInfo :product="product" class="md:col-span-2 hidden md:flex" />

						<InformationAboutHypermarket text="Informace pro" class="max-md:hidden col-span-2" :default-hm="DEFAULT_HYPERMARKET" />

						<div class="flex gap-2 md:gap-10 max-md:flex-wrap justify-between">
							<div>
								<ProductTriplePricing
									v-if="product.productInHouse"
									class="self-center"
									:product-in-house="product.productInHouse"
									only-large-price
								/>

								<span v-if="product.productInHouse?.priceValidTo && visiblePriceValidTo(product.productInHouse)" class="text-xs md:text-sm text-[#626262] mt-2 block">
									Platné do: {{ format(product.productInHouse.priceValidTo, 'd.M.') }}
								</span>
							</div>
							<ProductAmountInfo :product="product" class="flex md:hidden" />
							<ProductAvailabilityCard :product="product" class="max-w-[21.4rem] max-lg:w-full" />
						</div>
					</div>

					<div class="max-md:col-span-2 self-end max-md:mt-2 max-md:order-4 mb-4 md:mb-10">
						<ProductButtons
							v-model:is-shopping-list-open="isShoppingListOpen"
							:product="product"
							product-detail
							button-style
							method="product_detail"
						/>
					</div>
				</div>

				<ProductLoyaltyBanners :product="product" />

				<ProductDetailInfo :product="product" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import {format} from 'date-fns';
	import {storeToRefs} from 'pinia';
	import type {RouteLocationRaw} from 'vue-router';
	import {useCustomerProfileStore} from '~/stores/customerProfile';
	import {type Gsoa_ProductCatalog_ProductDetail as ProductDetail, ThisAndThatService} from '~/openapi';
	import {Badge} from '@/types/product';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {DEFAULT_HYPERMARKET, MYGLOBUSPRICES} from '#shared/constants';
	import {createHouseProductSlug} from '~/utils/createHouseProductSlug';
	import {useActionOffer} from '~/stores/actionOffer';
	import {useDeferredWatch} from '~/composables/useDeferredWatch';

	interface Props {
		product: ProductDetail;
		showMoreVariants?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {showMoreVariants: false});

	const category = useVariadicRouteParam('category');

	const actionOfferStore = useActionOffer();
	const {lastVisitedCategory} = storeToRefs(actionOfferStore);
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const customerProfileStore = useCustomerProfileStore();
	const {userIsLogged} = storeToRefs(customerProfileStore);
	const {selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const branchSpecificPath = useBranchSpecificPath();

	const isShoppingListOpen = ref(false);

	const houseNumber = computed(() => selectedHypermarketInstance.value?.content.gsoaId);

	const {data: moreVariants} = await useLazyAsyncData(
		`product-detail-more-variants-${props.product.vanr}-${houseNumber.value}`,
		async () => {
			if (!houseNumber.value || !props.product.vanr || !userIsLogged.value) {
				return null;
			}

			const response = await ThisAndThatService.thisAndThatGetProductsOnSaleCatalog({
				vanr: props.product.vanr,
				marketId: Number(houseNumber.value),
				strategy: lastVisitedCategory.value === MYGLOBUSPRICES.id ? 'bonus' : '',
			});

			return response.products ?? null;
		}, {watch: [houseNumber], server: false});

	// Add more variants badge additionally if applicable, because the product
	// wasn't fetched via This&That so the badge is never present
	const badges = computed(() =>
		props.product.badges
			? {
				...props.product.badges,
				...(moreVariants.value?.length ? createProductBadges(Badge.MORE_VARIANTS) : undefined),
			}
			: null,
	);

	function productVariantRouteGenerator(variant: ProductDetail): RouteLocationRaw {
		return branchSpecificPath({
			name: 'hypermarket-akcni-nabidka-category-p-product',
			params: {category: category.value, product: createHouseProductSlug(variant)},
		}).value;
	}

	useDeferredWatch(
		() => props.product?.vanr,
		() => {
			if (!props.product?.vanr) {
				return;
			}
			trackEcommerceEvent('view_item', {items: mapProductsToGtmEvent([props.product])});
		},
	);
</script>
